import { motion } from 'framer-motion';
import { useRef } from 'react';
import { useInView } from 'framer-motion';

const diagnosticItems = [
  {
    icon: 'ri-steering-2-line',
    title: 'Escáner de Labrado y Discos',
    subtitle: 'Medición digital de seguridad activa',
    description: 'Evaluación con herramienta de última generación del nivel de desgaste del labrado, estado de los discos de freno y alineación. Detectamos irregularidades que comprometen la estabilidad y el frenado antes de la RTM.',
    metrics: ['Profundidad del labrado (mm)', 'Estado superficial de discos', 'Desgaste irregular por eje', 'Presión de inflado calibrada'],
    image: 'https://public.readdy.ai/ai/img_res/1bb1f088a4e889f3908f29c5eaf8cbf4.jpg',
    imageAlt: 'Escáner digital de labrado y discos diagnóstico automotriz',
    color: 'green',
    badge: 'Seguridad activa',
  },
  {
    icon: 'ri-drop-line',
    title: 'Analizador Químico de Frenos',
    subtitle: 'Validación del estado del líquido de frenos',
    description: 'Análisis electroquímico del punto de ebullición y nivel de humedad del líquido de frenos. Un líquido degradado reduce la eficiencia de frenado hasta un 40% y es causal de rechazo en RTM.',
    metrics: ['Punto de ebullición (°C)', 'Porcentaje de humedad (%)', 'Estado del circuito hidráulico', 'Recomendación de cambio'],
    image: 'https://readdy.ai/api/search-image?query=automotive%20brake%20fluid%20chemical%20analyzer%20electronic%20tester%20device%20professional%20mechanic%20testing%20brake%20fluid%20quality%20in%20modern%20workshop%20Colombia%20industrial%20equipment%20close%20up%20dark%20background%20professional%20photography&width=600&height=380&seq=brake-analyzer-v3&orientation=landscape',
    imageAlt: 'Analizador químico de líquido de frenos',
    color: 'amber',
    badge: 'Análisis químico',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Evaluación Eléctrica Integral',
    subtitle: 'Diagnóstico con herramienta de última generación de batería, arranque y alternador',
    description: 'Diagnóstico electrónico completo del sistema eléctrico del vehículo. Evaluamos la capacidad real de la batería, el sistema de arranque y la eficiencia del alternador con equipos de última generación.',
    metrics: ['Voltaje en reposo (V)', 'Capacidad de carga (CCA)', 'Eficiencia del alternador', 'Estado del sistema de arranque'],
    image: 'https://readdy.ai/api/search-image?query=professional%20automotive%20electrical%20diagnostic%20equipment%20battery%20tester%20alternator%20analyzer%20connected%20to%20car%20engine%20in%20modern%20workshop%20Colombia%20technician%20using%20advanced%20diagnostic%20tool%20dark%20industrial%20background%20professional%20photography&width=600&height=380&seq=electrical-eval-v3&orientation=landscape',
    imageAlt: 'Evaluación eléctrica integral batería y alternador',
    color: 'blue',
    badge: 'Herramienta de última generación',
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  green: { bg: 'bg-[#28A745]/10', text: 'text-[#28A745]', border: 'border-[#28A745]/25', dot: 'bg-[#28A745]' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/25', dot: 'bg-amber-400' },
  blue: { bg: 'bg-sky-500/10', text: 'text-sky-400', border: 'border-sky-500/25', dot: 'bg-sky-400' },
};

export default function TechnicalEvidence() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="evidencia-tecnica" className="py-16 md:py-24 bg-[#E1E1E1]" ref={ref}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-600 text-xs font-bold tracking-wider uppercase mb-4">
            <i className="ri-error-warning-line"></i>
            3 fallas que te hacen reprobar
          </span>
          <h2 className="text-2xl md:text-4xl font-black text-[#1A2B3C] leading-tight mb-4 tracking-tight">
            Detectamos lo que otros no ven
          </h2>
          <p className="text-[#1A2B3C]/60 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            El 73% de los vehículos reprueban por estas 3 causas. Nosotros las detectamos antes con maquinaria especializada.
          </p>
        </motion.div>

        {/* Diagnostic Cards — 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {diagnosticItems.map((item, index) => {
            const c = colorMap[item.color];
            return (
              <motion.div
                key={index}
                className="bg-white border border-[#E1E1E1] rounded-2xl overflow-hidden hover:border-[#28A745]/30 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                {/* Image Space */}
                <div className="relative h-44 overflow-hidden bg-[#1A2B3C]/5">
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B3C]/40 to-transparent"></div>
                  <div className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 ${c.bg} border ${c.border} rounded-full backdrop-blur-sm`}>
                    <i className={`${item.icon} ${c.text} text-xs`}></i>
                    <span className={`${c.text} text-[10px] font-bold tracking-wider uppercase`}>{item.badge}</span>
                  </div>
                  {/* GRATIS badge */}
                  <div className="absolute bottom-3 right-3">
                    <motion.div
                      className="px-3 py-1.5 bg-[#28A745] rounded-lg shadow-lg"
                      animate={{ scale: [1, 1.06, 1] }}
                      transition={{ duration: 1.8, repeat: Infinity }}
                    >
                      <span className="text-white font-black text-sm tracking-widest">GRATIS</span>
                    </motion.div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5">
                  <div className={`w-10 h-10 flex items-center justify-center ${c.bg} rounded-xl mb-3`}>
                    <i className={`${item.icon} ${c.text} text-xl`}></i>
                  </div>
                  <h3 className="text-[#1A2B3C] font-bold text-base mb-1">{item.title}</h3>
                  <p className={`${c.text} text-xs font-semibold mb-3`}>{item.subtitle}</p>
                  <p className="text-[#1A2B3C]/55 text-xs leading-relaxed mb-4">{item.description}</p>

                  {/* Metrics */}
                  <div className="space-y-2">
                    <p className="text-[#1A2B3C]/35 text-[10px] uppercase tracking-wider font-bold">Parámetros evaluados</p>
                    {item.metrics.map((metric, mIndex) => (
                      <div key={mIndex} className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`}></div>
                        <span className="text-[#1A2B3C]/65 text-xs">{metric}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Data-driven footer */}
                <div className="px-5 pb-5">
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${c.bg} border ${c.border}`}>
                    <i className={`ri-database-2-line text-sm ${c.text}`}></i>
                    <span className={`text-[10px] font-semibold ${c.text}`}>Medición con instrumento técnico verificado</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom Banner */}
        <motion.div
          className="bg-[#1A2B3C] rounded-2xl p-6 md:p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-white font-bold text-lg md:text-xl mb-2">
                Resultados entregados con evidencia técnica real
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Cada diagnóstico genera un reporte con los valores medidos, el estado actual y las recomendaciones técnicas específicas para tu vehículo antes de la RTM.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { value: '3', label: 'Sistemas evaluados' },
                { value: '100%', label: 'Datos reales' },
                { value: 'CDA', label: 'Centro autorizado' },
              ].map((stat, index) => (
                <div key={index} className="text-center px-5 py-3 bg-white/8 rounded-xl border border-white/10 min-w-[90px]">
                  <p className="text-[#28A745] font-black text-xl">{stat.value}</p>
                  <p className="text-white/40 text-[10px] mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
